import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { tenantService, Tenant, TenantCreate, TenantUpdate } from '../../services/tenantService';

interface TenantFormProps {
  tenant?: Tenant | null;
  onClose: () => void;
}

const TenantForm = ({ tenant, onClose }: TenantFormProps) => {
  const queryClient = useQueryClient();
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<TenantCreate | TenantUpdate>({
    defaultValues: tenant || {
      name: '',
      domain: '',
      timezone: 'America/Mexico_City',
      currency: 'MXN'
    }
  });

  useEffect(() => {
    if (tenant) {
      reset(tenant);
    }
  }, [tenant, reset]);

  const createMutation = useMutation(tenantService.create, {
    onSuccess: () => {
      toast.success('Tenant creado correctamente');
      queryClient.invalidateQueries(['tenants']);
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Error al crear tenant');
    }
  });

  const updateMutation = useMutation(
    (data: TenantUpdate) => tenantService.update(tenant!.id, data),
    {
      onSuccess: () => {
        toast.success('Tenant actualizado correctamente');
        queryClient.invalidateQueries(['tenants']);
        onClose();
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Error al actualizar tenant');
      }
    }
  );

  const onSubmit = (data: TenantCreate | TenantUpdate) => {
    if (tenant) {
      updateMutation.mutate(data as TenantUpdate);
    } else {
      createMutation.mutate(data as TenantCreate);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h2 className="text-xl font-bold mb-4">
          {tenant ? 'Editar Tenant' : 'Nuevo Tenant'}
        </h2>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nombre *
            </label>
            <input
              {...register('name', { required: 'Nombre es requerido' })}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {errors.name && (
              <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Domain
            </label>
            <input
              {...register('domain')}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Timezone
            </label>
            <select
              {...register('timezone')}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="America/Mexico_City">America/Mexico_City</option>
              <option value="America/New_York">America/New_York</option>
              <option value="America/Los_Angeles">America/Los_Angeles</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Moneda
            </label>
            <select
              {...register('currency')}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="MXN">MXN - Peso Mexicano</option>
              <option value="USD">USD - Dólar Americano</option>
            </select>
          </div>

          {tenant && (
            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  {...register('isActive')}
                  className="rounded"
                />
                <span className="text-sm font-medium text-gray-700">Activo</span>
              </label>
            </div>
          )}

          <div className="flex justify-end space-x-2 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={createMutation.isLoading || updateMutation.isLoading}
              className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
            >
              {createMutation.isLoading || updateMutation.isLoading ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TenantForm;

